import { storeToRefs } from "pinia";

import { useFilterStore } from "@/stores/modules/filter";
import RecommendationApi from "@/api/RecommendationApi";
import { useI18n } from "vue-i18n";

export const useFilter = () => {
  const filterStore = useFilterStore();
  const {
    results,
    filters,
    pageConfig,
    currentPage,
    count,
    nextPage,
    loading
  } = storeToRefs(filterStore);

    const { t } = useI18n();

  const getConfig = async data => {
    await filterStore.getConfig(data);
  };

  const getData = async (filters, initialConfig = {}) => {
    loading.value = true;
    let params = {
      page: currentPage.value || filters.page,
      page_size: 24,
      count: true,
      ...initialConfig
    };
    if (filters.recommendedConcerts) {
      const rec = await RecommendationApi.getRecommendations({ slug: "conciertos-destacados-para-ti" });
      console.log("rec", rec);
      // Busca el bloque con el slug correcto
      const block = rec.recommendation?.find(
        b => b.slug === "conciertos-destacados-para-ti"
      );
      const events = block?.features || [];
      console.log("events", events);
      filterStore.results = events;
      filterStore.count = events.length;
      filterStore.nextPage = null;
      filterStore.pageConfig = {
        title: t("wegow.filters.recommendedConcerts"),
        type: "concerts"
      };
      loading.value = false;
      return;
    }
    if (
      filters.type === "concerts" ||
      filters.type === "festivals" ||
      filters.type === "both"
    ) {
      params.query = filters.query ? filters.query : undefined;
      params.type =
        filters.type === "concerts" ? 0 : filters.type === "festivals" ? 1 : 2;
      if (
        filters.cities &&
        Array.isArray(filters.cities) &&
        filters.cities.length &&
        filters.cities[0].id
      ) {
        // Las ciudades estan hechas para google
        params.cities =
          filters.cities?.map(city => city.id).join("|") || undefined;
        params.country = undefined;
        params.city = undefined;
        params.administrative_division = undefined;
      }
      if (filters.genres && Array.isArray(filters.genres)) {
        params.genres = filters.genres?.join("|") || undefined;
      }

      if (
        filters.venues &&
        Array.isArray(filters.venues) &&
        filters.venues.length &&
        filters.venues[0].id
      ) {
        params.venue =
          filters.venues?.map(venue => venue.id).join("|") || undefined;
      }
      if (filters.date) {
        params = { ...params, ...getFilterDate(filters) };
      }

      // Add extra
      if (filters.bono_cultural) {
        params.sellbyus = true;
        params.cou_code = "ES";
      } else if (filters.sellbyus) {
        params.sellbyus = true;
      }

      // Fixed
      params.sda = "desktop-filters-events";
    } else if (filters.type === "artists") {
      params.query = filters.query;
      if (filters.genres && Array.isArray(filters.genres)) {
        params.genres = filters.genres?.join("|") || undefined;
      }
    } else if (filters.type === "users") {
      params.query = filters.query;
    } else if (filters.type === "venues") {
      params.query = filters.query;
      if (
        filters.cities &&
        Array.isArray(filters.cities) &&
        filters.cities.length &&
        filters.cities[0].id
      ) {
        // Las ciudades estan hechas para google
        params.cities =
          filters.cities?.map(city => city.id).join("|") || undefined;
        params.country = undefined;
        params.city = undefined;
        params.administrative_division = undefined;
      }
    } else if (filters.type === "pages") {
      params.query = filters.query;
    }
    pageConfig.value.type = filters.type;
    await filterStore.getData(params, filters.type);
    loading.value = false;
  };

  const getFilterDate = filters => {
    const today = new Date();
    let params = {};
    if (filters.date === "personalized") {
      if (filters.from_date) {
        params.from_date = new Date(filters.from_date).toISOString();
      }
      if (filters.to_date) {
        params.to_date = new Date(filters.to_date).toISOString();
      }
    } else if (filters.date === "today") {
      const todayFormat = today.toISOString().slice(0, 10);
      params.from_date = todayFormat;
      params.to_date = todayFormat;
    } else if (filters.date === "tomorrow") {
      const tomorrow = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + 2
      )
        .toISOString()
        .slice(0, 10);
      params.from_date = tomorrow;
      params.to_date = tomorrow;
    } else if (filters.date === "week") {
      const firstDayOfWeek = new Date(
        today.setDate(today.getDate() - today.getDay() + 1)
      )
        .toISOString()
        .slice(0, 10);
      const lastDayOfWeek = new Date(
        today.setDate(today.getDate() - today.getDay() + 7)
      )
        .toISOString()
        .slice(0, 10);

      params.from_date = firstDayOfWeek;
      params.to_date = lastDayOfWeek;
    } else if (filters.date === "weekend") {
      const currentDayOfWeek = today.getDay();
      const daysToAddForFriday =
        currentDayOfWeek <= 5 ? 5 - currentDayOfWeek : 12 - currentDayOfWeek;
      const daysToAddForSunday =
        currentDayOfWeek <= 0 ? 0 - currentDayOfWeek + 7 : 7 - currentDayOfWeek;

      const friday = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + daysToAddForFriday + 1
      );
      const sunday = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + daysToAddForSunday + 1
      );

      const fridayFormatted = friday.toISOString().slice(0, 10);
      const sundayFormatted = sunday.toISOString().slice(0, 10);

      params.from_date = fridayFormatted;
      params.to_date = sundayFormatted;
    } else if (filters.date === "month") {
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 2)
        .toISOString()
        .slice(0, 10);

      const lastDayOfMonth = new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        1
      )
        .toISOString()
        .slice(0, 10);

      params.from_date = firstDayOfMonth;
      params.to_date = lastDayOfMonth;
    }
    return params;
  };

  const changePage = newPage => {
    currentPage.value = Number(newPage);
    window.scrollTo(0, 0);
  };

  return {
    //! Properties
    results,
    filters,
    pageConfig,
    currentPage,
    nextPage,
    loading,
    count,
    //! Metodos
    getConfig,
    getData,
    changePage
  };
};
